// 表单校验用：整串必须就是一个手机号，故保留 ^$ 锚点
const MOBILE_REGEX = /^1[3-9]\d{9}$/;

export function isValidMobile(mobile: string): boolean {
  return MOBILE_REGEX.test(mobile);
}

export function isValidPassword(password: string): boolean {
  if (!password || password.length < 8) return false;
  const hasLetter = /[a-zA-Z]/.test(password);
  const hasNumber = /\d/.test(password);
  return hasLetter && hasNumber;
}

export function getPasswordStrength(password: string): 'weak' | 'medium' | 'strong' {
  if (!isValidPassword(password)) return 'weak';
  let score = 0;
  if (password.length >= 12) score++;
  if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score++;
  if (/\d/.test(password)) score++;
  if (/[^a-zA-Z0-9]/.test(password)) score++;
  if (score >= 3) return 'strong';
  if (score >= 2) return 'medium';
  return 'weak';
}

// ===== 敏感信息检测（用户反馈、帮助中心等自由文本使用） =====
// 本文件与移动端 ledger-ai-app/src/shared/utils/validators.ts 是同一份实现，改一端必须同批改另一端。

/**
 * 三条规则一律用「数字边界」而非 \b，且按 手机号 → 身份证 → 银行卡 的优先级依次消费：
 * 前一段命中后把那段数字替换成等长非数字，后面的规则不会再对同一串重复上报
 * （修复审查待定项#3：18 位身份证同时满足银行卡的 16-19 位规则，此前会报两种类型）。
 * 断言不用后顾 (?<!\d)：iOS Safari < 16.4 与微信小程序不支持，故把前导非数字写成捕获组。
 */
const MOBILE_PATTERN = /(^|\D)(1[3-9]\d{9})(?=\D|$)/g;
const ID_CARD_PATTERN = /(^|\D)(\d{17}[\dXx])(?=\D|$)/g;
const BANK_CARD_PATTERN = /(^|\D)(\d{16,19})(?=\D|$)/g;

const SENSITIVE_RULES: ReadonlyArray<readonly [RegExp, string]> = [
  [MOBILE_PATTERN, '手机号'],
  [ID_CARD_PATTERN, '身份证号'],
  [BANK_CARD_PATTERN, '银行卡号'],
];

interface MaskResult {
  rest: string;
  hit: boolean;
}

/** 把文本里所有该类号码遮住（等长替换，保持其余位置的分隔关系不变） */
function maskKind(text: string, pattern: RegExp): MaskResult {
  let hit = false;
  const rest = text.replace(pattern, (_all, lead: string, matched: string) => {
    hit = true;
    return `${lead}${'#'.repeat(matched.length)}`;
  });
  return { rest, hit };
}

export interface SensitiveInfoResult {
  hasSensitive: boolean;
  types: string[];
  message: string;
}

/**
 * 检测文本中的敏感信息
 * 用于用户反馈提交时的脱敏提示
 */
export function detectSensitiveInfo(text: string): SensitiveInfoResult {
  const types: string[] = [];
  let rest = text;
  for (const [pattern, label] of SENSITIVE_RULES) {
    const masked = maskKind(rest, pattern);
    if (masked.hit) types.push(label);
    rest = masked.rest;
  }

  if (types.length > 0) {
    return {
      hasSensitive: true,
      types,
      message: `检测到输入内容包含${types.join('、')}，建议脱敏后提交。`,
    };
  }

  return { hasSensitive: false, types: [], message: '' };
}
